/**
 * The grown-up gate (spec §Settings — `docs/proposals/ai-personas-voice-and-sound.md`):
 * "The parent area can lock these: turn voices, chatter or music off for the device, and cap
 * volume. A locked setting is not changeable from the game."
 *
 * The app has no parent account on a shared device, so the grown-up area opens behind a short
 * arithmetic question — the pattern every children's app uses. It is NOT a password and it is NOT
 * a security boundary: it is a speed bump for a five-year-old. What it protects is a SETTING, not
 * a secret, and the locks it sets are enforced in the effective audio settings (see
 * `effectiveAudio`), not by hiding a control.
 *
 * The numbers stay small (4..9) so a seven-year-old can answer it — a gate nobody can pass is
 * not a gate, it is a broken screen.
 */

export interface GateQuestion {
  readonly a: number;
  readonly b: number;
  readonly answer: number;
}

/** The smallest and largest addend. Small on purpose. */
export const GATE_MIN = 4;
export const GATE_MAX = 9;

/** A fresh addition question. `random` is a seam, so a test can pin the numbers. */
export function makeGateQuestion(random: () => number = Math.random): GateQuestion {
  const span = GATE_MAX - GATE_MIN + 1;
  const pick = () => GATE_MIN + Math.floor(random() * span);
  const a = pick();
  const b = pick();
  return { a, b, answer: a + b };
}

/** Is the typed answer the right one? Only a plain whole number counts. */
export function gateAnswer(question: GateQuestion, raw: string): boolean {
  const trimmed = raw.trim();
  if (!/^\d{1,3}$/.test(trimmed)) return false;
  return Number(trimmed) === question.answer;
}
